import { marked } from 'marked';
import htmlToPdfmake from 'html-to-pdfmake';
import * as pdfMake from 'pdfmake/build/pdfmake';
import * as pdfFonts from 'pdfmake/build/vfs_fonts';
import logoImg from '../assets/images/logoHeader.png';
import { Message } from '../interfaces/components/chatList';

// Attach the vfs fonts to pdfMake
(pdfMake as any).vfs = pdfFonts.pdfMake?.vfs;

// Register Arabic-supporting font
(pdfMake as any).fonts = {
  Roboto: {
    normal: 'https://cdnjs.cloudflare.com/ajax/libs/pdfmake/0.1.66/fonts/Roboto/Roboto-Regular.ttf',
    bold: 'https://cdnjs.cloudflare.com/ajax/libs/pdfmake/0.1.66/fonts/Roboto/Roboto-Medium.ttf',
    italics: 'https://cdnjs.cloudflare.com/ajax/libs/pdfmake/0.1.66/fonts/Roboto/Roboto-Italic.ttf',
    bolditalics: 'https://cdnjs.cloudflare.com/ajax/libs/pdfmake/0.1.66/fonts/Roboto/Roboto-MediumItalic.ttf',
  },
};

const getDataUrl = async (url: string): Promise<string> => {
  const response = await fetch(url);
  const blob = await response.blob();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};

// Helper to remove margins and adjust font size for heading and paragraph tags.
const adjustHtml = (html: string, fontSize: string): string => {
  let result = html.replace(/<(h[1-6])([^>]*)>/g, `<$1 style="margin:0; font-size: 16px;">`);
  result = result.replace(/<p([^>]*)>/g, `<p style="margin:0; font-size:${fontSize};">`);
  result = result.replace(/<li([^>]*)>/g, `<li style="margin:0; font-size:${fontSize};">`);
  return result;
};

export const exportChatToPdf = async (list: Message[]) => {
  try {
    const messages = [...(list || [])].reverse();
    // Convert the logo to a base64 string.
    const logoDataUrl = await getDataUrl(logoImg);

    // Reorder messages so that each pair is [user question, assistant answer].
    const pairedMessages: { question?: Message; answer?: Message }[] = [];
    let i = 0;
    while (i < messages.length) {
      if (i + 1 < messages.length) {
        const first = messages[i];
        const second = messages[i + 1];
        if (first.role === 'assistant' && second.role === 'user') {
          pairedMessages.push({ question: second, answer: first });
          i += 2;
          continue;
        }
        if (first.role === 'user' && second.role === 'assistant') {
          pairedMessages.push({ question: first, answer: second });
          i += 2;
          continue;
        }
      }
      const msg = messages[i];
      if (msg.role === 'user') {
        pairedMessages.push({ question: msg });
      } else {
        pairedMessages.push({ answer: msg });
      }
      i++;
    }

    // Create PDF content array
    const pdfContent: any[] = [];

    // Add logo
    const logoSection = htmlToPdfmake(`
      <div style="text-align: center; margin-bottom: 20px;">
        <img src="${logoDataUrl}" style="width:150px; height:50px;" alt="Logo"/>
      </div>
    `);
    pdfContent.push(logoSection);

    // Build conversation blocks
    pairedMessages.forEach((pair, index) => {
      const conversationHtml = [];

      if (pair.question) {
        const questionHtml = marked(pair.question.content, { breaks: true, gfm: true });
        const adjustedQuestionHtml = adjustHtml(questionHtml as string, '20px');
        conversationHtml.push(
          `<div style="font-weight: bold; color: #000000; margin-bottom: 5px;">${adjustedQuestionHtml}</div>`,
        );
      }

      if (pair.answer) {
        const answerHtml = marked(pair.answer.content, { breaks: true, gfm: true });
        const adjustedAnswerHtml = adjustHtml(answerHtml as string, '14px');
        conversationHtml.push(`<div style="color: #000000;">${adjustedAnswerHtml}</div>`);

        if (pair.answer.references?.length) {
          conversationHtml.push(`<h5 style="margin-top: 10px;">References</h5><ol>`);
          pair.answer.references.forEach((ref) => {
            conversationHtml.push(`<li>${ref.full_link}</li>`);
          });
          conversationHtml.push(`</ol>`);
        }
      }

      // Convert the conversation block to pdfmake format
      const conversationBlock = htmlToPdfmake(conversationHtml.join(''));
      pdfContent.push(conversationBlock);

      // Add page break after each conversation block except the last one
      if (index < pairedMessages.length - 1) {
        pdfContent.push({ text: '', pageBreak: 'after' });
      }
    });

    // Build the pdfmake document definition.
    const docDefinition = {
      content: pdfContent,
      pageSize: 'A4',
      pageMargins: [40, 40, 40, 40],
      defaultStyle: {
        fontSize: 10,
        font: 'Roboto',
      },
    };

    // Create and open the PDF.
    pdfMake.createPdf(docDefinition).open();
  } catch (error) {
    console.error('Error generating PDF:', error);
  }
};
